import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import {
  PresentationForm,
  PresentationNotice,
  PresentationTimer,
  totalScore,
  usePresentationDrafts,
} from '../../../../components/Tournament/PresentationScoring';
import RowActions from '../../../../components/Tournament/RowActions';
import TimeInput from '../../../../components/Tournament/TimeInput';
import { useToast } from '../../../../hooks/toast';
import api, { getApiErrorMessage } from '../../../../services/api';
import { media } from '../../../../styles/brand';
import {
  buildWallClockIso,
  formatWallClockTime,
} from '../../../../utils/scheduleDate';
import { categoryLabel } from '../../../../utils/tournament/labels';
import {
  agendaMarks,
  wallClockNow,
} from '../../../../utils/tournament/presentationAgenda';
import useNow from '../../../TournamentBoard/hooks/useNow';
import {
  agendaTeams,
  applySaved,
  IAgendaTeam,
  IPresentationSchedule,
  IScheduledPresentation,
  IScheduleRoom,
  nextPendingTeam,
  savedScores,
  savePresentation,
  toAgendaItems,
} from './agendaState';
import EvaluationSheet from './EvaluationSheet';
import {
  AgendaColumn,
  Button,
  Columns,
  Editor,
  EditorActions,
  Empty,
  ErrorText,
  FormColumn,
  Hint,
  Mark,
  Points,
  Room,
  RoomHeader,
  Row,
  Rows,
  Side,
  Summary,
  Team,
  Time,
  Wrapper,
} from './styles';

export type { IPresentationSchedule } from './agendaState';

interface IPresentationAgendaProps {
  tournamentId: string;
  /** Organizador e juízes do torneio podem remarcar (mesma regra da API). */
  canEdit: boolean;
}

interface IEditing {
  slot_id: string;
  time: string;
  room_id: string;
  swap_with: string;
}

const describeTeam = (team: IAgendaTeam): string =>
  [
    team.slot
      ? `${formatWallClockTime(team.slot.starts_at)}–${formatWallClockTime(
          team.slot.ends_at,
        )} · ${team.slot.room_name}`
      : 'Sem horário',
    team.school_name,
    categoryLabel(team.category),
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Aba Apresentações (etapa nacional): a agenda da sala e a avaliação da banca
 * no mesmo lugar (docs/avaliacao-apresentacao-aba-tasks.md).
 *
 * - Desktop: agenda à esquerda, formulário da equipe escolhida à direita.
 * - Celular: "Avaliar" abre a folha em tela cheia.
 * - Rascunho e cronômetro por equipe ficam no `usePresentationDrafts`: trocar
 *   de aba, de equipe ou recarregar não perde nada.
 * - "Mudar" remarca uma equipe ou troca duas de horário (FE-59).
 */
const PresentationAgenda: React.FC<IPresentationAgendaProps> = ({
  tournamentId,
  canEdit,
}) => {
  const { addToast } = useToast();
  const isMobile = useMediaQuery({ query: media.md });
  const drafts = usePresentationDrafts(tournamentId);
  const nowWall = wallClockNow(useNow(30_000));

  const [schedule, setSchedule] = useState<IPresentationSchedule | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    teamId: string;
    message: string;
  } | null>(null);
  const [editing, setEditing] = useState<IEditing | null>(null);
  const [moving, setMoving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<IPresentationSchedule>(
        `/tournaments/${tournamentId}/presentation-schedule`,
      )
      .then(({ data }) => {
        setSchedule(data);
        setLoadError(null);
        // Abre na equipe da vez; mantém a escolha se já houver uma.
        setSelectedId(
          current => current ?? nextPendingTeam(data, wallClockNow()),
        );
      })
      .catch(err =>
        setLoadError(
          getApiErrorMessage(err) ??
            'Não foi possível carregar a agenda das apresentações.',
        ),
      );
  }, [tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  const teams = useMemo(
    () => (schedule ? agendaTeams(schedule) : []),
    [schedule],
  );
  const marks = useMemo(
    () => agendaMarks(toAgendaItems(teams), nowWall),
    [teams, nowWall],
  );
  const selected = teams.find(team => team.team_id === selectedId) ?? null;
  const scoredCount = teams.filter(team => team.scored).length;

  const pendingSlots = useMemo(
    () =>
      (schedule?.rooms ?? []).flatMap(room =>
        room.presentations.filter(p => !p.scored),
      ),
    [schedule],
  );

  const openEvaluation = (teamId: string) => {
    setSelectedId(teamId);
    setSaveError(null);
    if (isMobile) setSheetOpen(true);
  };

  const save = async () => {
    if (!schedule || !selected) return;
    const team = selected;
    const scores = drafts.scoresOf(team.team_id, savedScores(team));

    setSaving(true);
    setSaveError(null);
    const result = await savePresentation(
      api,
      tournamentId,
      team.team_id,
      scores,
    );
    setSaving(false);

    if ('message' in result) {
      // O rascunho fica: nada do que a banca digitou se perde (FE-69).
      setSaveError({ teamId: team.team_id, message: result.message });
      return;
    }

    const updated = applySaved(schedule, team.team_id, scores);
    setSchedule(updated);
    drafts.clearScores(team.team_id);
    addToast({
      type: 'success',
      expression: 'Avaliação salva',
      description: `${team.team_name}: ${totalScore(scores)} pontos.`,
    });

    const next = nextPendingTeam(updated, wallClockNow(), team.team_id);
    if (isMobile) {
      setSheetOpen(false);
      if (next) setSelectedId(next);
    } else if (next) {
      setSelectedId(next);
    }
  };

  const startEditing = (room: IScheduleRoom, p: IScheduledPresentation) => {
    setEditError(null);
    setEditing({
      slot_id: p.slot_id,
      time: formatWallClockTime(p.starts_at),
      room_id: room.id,
      swap_with: '',
    });
  };

  const submitMove = async (
    event: React.FormEvent,
    p: IScheduledPresentation,
  ) => {
    event.preventDefault();
    if (!editing) return;
    setMoving(true);
    setEditError(null);

    try {
      const { data } = editing.swap_with
        ? await api.post<IPresentationSchedule>(
            `/tournaments/${tournamentId}/presentation-schedule/swap`,
            { slot_id: p.slot_id, other_slot_id: editing.swap_with },
          )
        : await api.patch<IPresentationSchedule>(
            `/tournaments/${tournamentId}/presentation-schedule/${p.slot_id}`,
            {
              // Hora de parede, sem 'Z' — mesmo formato do cronograma.
              starts_at: buildWallClockIso(p.starts_at, editing.time),
              room_id: editing.room_id,
            },
          );

      setSchedule(data);
      setEditing(null);
      addToast({
        type: 'success',
        expression: 'Agenda atualizada',
        description: editing.swap_with
          ? `${p.team_name} trocou de horário.`
          : `${p.team_name} às ${editing.time}.`,
      });
    } catch (err) {
      setEditError(
        getApiErrorMessage(err) ?? 'Não foi possível mudar o horário.',
      );
    } finally {
      setMoving(false);
    }
  };

  if (loadError) return <Empty>{loadError}</Empty>;
  if (!schedule) return <Empty>Carregando agenda…</Empty>;

  if (teams.length === 0) {
    return (
      <Empty>Nenhuma equipe com apresentação técnica neste torneio.</Empty>
    );
  }

  const form = selected && (
    <PresentationForm
      key={selected.team_id}
      teamName={selected.team_name}
      subtitle={describeTeam(selected)}
      scores={drafts.scoresOf(selected.team_id, savedScores(selected))}
      alreadyScored={selected.scored}
      saving={saving}
      onChange={scores => drafts.setScores(selected.team_id, scores)}
      onSave={save}
      stickyFooter={isMobile}
      hideTitle={isMobile}
      timer={
        <PresentationTimer
          timer={drafts.timerOf(selected.team_id)}
          onStart={() => drafts.start(selected.team_id)}
          onPause={() => drafts.pause(selected.team_id)}
          onReset={() => drafts.reset(selected.team_id)}
        />
      }
      notice={
        <>
          {selected.disqualified && (
            <PresentationNotice $tone="warning">
              Equipe desclassificada: a nota fica registrada, mas a equipe não
              disputa a classificação.
            </PresentationNotice>
          )}
          {saveError?.teamId === selected.team_id && (
            <PresentationNotice $tone="error" role="alert">
              <span>{saveError.message}</span>
              <Button type="button" onClick={save} disabled={saving}>
                Tentar de novo
              </Button>
            </PresentationNotice>
          )}
        </>
      }
    />
  );

  const evaluateAction = (team: IAgendaTeam, isNext = false) => (
    <>
      {team.scored && <Points>{team.scores?.total_score ?? 0} pts</Points>}
      <Button
        type="button"
        $primary={!team.scored && isNext}
        $quiet={!team.scored && !isNext}
        aria-pressed={!isMobile && selectedId === team.team_id}
        onClick={() => openEvaluation(team.team_id)}
      >
        {team.scored ? 'Editar nota' : 'Avaliar'}
      </Button>
    </>
  );

  const agenda = (
    <AgendaColumn>
      {schedule.rooms.length === 0 && (
        <Hint>
          A agenda das salas ainda não foi montada; as equipes aparecem em “Sem
          horário” e já podem ser avaliadas.
        </Hint>
      )}

      {schedule.rooms.map(room => {
        const first = room.presentations[0];
        const last = room.presentations[room.presentations.length - 1];

        return (
          <Room key={room.id} aria-label={room.name}>
            <RoomHeader>
              <h3>{room.name}</h3>
              {first && last && (
                <span>
                  {formatWallClockTime(first.starts_at)}–
                  {formatWallClockTime(last.ends_at)}
                </span>
              )}
            </RoomHeader>

            <Rows>
              {room.presentations.map(p => {
                const team = teams.find(t => t.team_id === p.team_id);
                const mark = marks[p.team_id] ?? null;
                const isEditing = editing?.slot_id === p.slot_id;

                return (
                  <Row
                    key={p.slot_id}
                    $mark={mark}
                    $selected={!isMobile && selectedId === p.team_id}
                  >
                    <Time dateTime={p.starts_at}>
                      {formatWallClockTime(p.starts_at)}–
                      {formatWallClockTime(p.ends_at)}
                    </Time>
                    <Team>
                      <strong>
                        {p.team_name}
                        {mark && (
                          <Mark $mark={mark}>
                            {mark === 'now' ? 'Agora' : 'A seguir'}
                          </Mark>
                        )}
                      </strong>
                      <small>
                        {[p.school_name, categoryLabel(p.category)]
                          .filter(Boolean)
                          .join(' · ')}
                        {p.disqualified ? ' · desclassificada' : ''}
                        {drafts.hasDraft(p.team_id) ? ' · rascunho' : ''}
                      </small>
                    </Team>
                    <Side>
                      {team && evaluateAction(team, mark !== null)}
                      {canEdit && !p.scored && !isEditing && (
                        <RowActions
                          label={`Ações de ${p.team_name}`}
                          actions={[
                            {
                              label: 'Mudar horário…',
                              onSelect: () => startEditing(room, p),
                            },
                          ]}
                        />
                      )}
                    </Side>

                    {isEditing && editing && (
                      <Editor onSubmit={event => submitMove(event, p)}>
                        <label>
                          Novo horário
                          <TimeInput
                            aria-label={`Novo horário de ${p.team_name}`}
                            value={editing.time}
                            disabled={!!editing.swap_with}
                            onChange={time => setEditing({ ...editing, time })}
                          />
                        </label>

                        {schedule.rooms.length > 1 && (
                          <label>
                            Sala
                            <select
                              value={editing.room_id}
                              disabled={!!editing.swap_with}
                              onChange={e =>
                                setEditing({
                                  ...editing,
                                  room_id: e.target.value,
                                })
                              }
                            >
                              {schedule.rooms.map(r => (
                                <option key={r.id} value={r.id}>
                                  {r.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}

                        <label>
                          Ou trocar com
                          <select
                            value={editing.swap_with}
                            onChange={e =>
                              setEditing({
                                ...editing,
                                swap_with: e.target.value,
                              })
                            }
                          >
                            <option value="">Ninguém</option>
                            {pendingSlots
                              .filter(other => other.slot_id !== p.slot_id)
                              .map(other => (
                                <option
                                  key={other.slot_id}
                                  value={other.slot_id}
                                >
                                  {formatWallClockTime(other.starts_at)} ·{' '}
                                  {other.team_name}
                                </option>
                              ))}
                          </select>
                        </label>

                        <EditorActions>
                          <Button
                            type="button"
                            onClick={() => setEditing(null)}
                            disabled={moving}
                          >
                            Cancelar
                          </Button>
                          <Button type="submit" $primary disabled={moving}>
                            {moving
                              ? 'Salvando…'
                              : editing.swap_with
                                ? 'Trocar'
                                : 'Salvar'}
                          </Button>
                        </EditorActions>

                        {editError && (
                          <ErrorText role="alert">{editError}</ErrorText>
                        )}
                      </Editor>
                    )}
                  </Row>
                );
              })}
            </Rows>
          </Room>
        );
      })}

      {schedule.unscheduled.length > 0 && (
        <Room aria-label="Sem horário">
          <RoomHeader>
            <h3>Sem horário</h3>
            <span>{schedule.unscheduled.length} equipe(s)</span>
          </RoomHeader>
          <Rows>
            {teams
              .filter(team => !team.slot)
              .map(team => (
                <Row
                  key={team.team_id}
                  $mark={null}
                  $selected={!isMobile && selectedId === team.team_id}
                >
                  <Time as="span">—</Time>
                  <Team>
                    <strong>{team.team_name}</strong>
                    <small>
                      {[team.school_name, categoryLabel(team.category)]
                        .filter(Boolean)
                        .join(' · ')}
                      {drafts.hasDraft(team.team_id) ? ' · rascunho' : ''}
                    </small>
                  </Team>
                  <Side>{evaluateAction(team)}</Side>
                </Row>
              ))}
          </Rows>
        </Room>
      )}
    </AgendaColumn>
  );

  return (
    <Wrapper>
      <Summary>
        <span>
          <strong>
            {scoredCount} de {teams.length}
          </strong>{' '}
          avaliadas
        </span>
        {canEdit && <Hint>Equipes já avaliadas não mudam de horário.</Hint>}
      </Summary>

      {isMobile ? (
        <>
          {agenda}
          <EvaluationSheet
            open={sheetOpen && !!selected}
            title={selected?.team_name ?? ''}
            subtitle={selected ? describeTeam(selected) : ''}
            onClose={() => setSheetOpen(false)}
          >
            {form}
          </EvaluationSheet>
        </>
      ) : (
        <Columns>
          {agenda}
          <FormColumn>
            {form ?? <Empty>Escolha uma equipe na agenda para avaliar.</Empty>}
          </FormColumn>
        </Columns>
      )}
    </Wrapper>
  );
};

export default PresentationAgenda;
